import { CachePersistor, LocalStorageWrapper, PersistentStorage } from 'apollo3-cache-persist';

import { APPLICATION_VERSION } from '@src/setup/dictionary';
import { cache } from '@store/apollo/cache';

/**
 * Apollo cache persistence.
 *
 * Goals:
 *   1. Restored read-throughs after a page reload — the user opens the same dashboard
 *      and sees data immediately while a `cache-and-network` query revalidates.
 *   2. Don't persist anything that's auth-sensitive, transient, or already mirrored
 *      elsewhere (reactive vars, subscription buffers, large file lists).
 *   3. Bust the persisted cache on every deploy that bumps APPLICATION_VERSION so
 *      schema changes never produce a hydration mismatch.
 *
 * Implementation notes:
 *   - We use localStorage (not IndexedDB) for portability and synchronous boot reads.
 *     5MB is plenty for normalized GraphQL data; we cap at ~3MB to leave headroom for
 *     other app storage (oidc.user, theme, cart).
 *   - `persistenceMapper` runs on every persist tick and lets us drop entire entities
 *     from the persisted snapshot — the in-memory cache is unaffected.
 *   - `trigger: 'background'` writes when the tab is hidden, which is much cheaper
 *     than write-on-every-mutation and avoids jank during user interaction.
 */

const STORAGE_KEY = 'apollo-cache-persist';
const SCHEMA_VERSION_KEY = 'apollo-cache-persist:version';
const MAX_SIZE_BYTES = 3 * 1024 * 1024; // 3MB

// Typenames that should NEVER be persisted. Either they're transient, sensitive,
// already mirrored in a reactive var, or large enough to blow the size budget on
// their own. Adding to this list is the safer default — only persist what you'd be
// happy to render stale on a reload.
const NON_PERSISTED_TYPENAMES = new Set<string>([
  // Reactive-var-backed (already persisted via storage)
  'UserSessionData',
  'UserCartData',
  'UserEventData',
  'UserAlerts',
  'UserWishListData',
  'LayoutStore',
  'NavbarStore',
  'Notifications',
  'Tasks',
  'Messages',
  'IsLoggedIn',

  // Auth-sensitive — must round-trip through the gateway every time
  'TwoFactorAuthentication',
  'TwoFactorPush',
  'TwoFactorTotp',
  'PractitionerSignatures',
  'OrderSignatures',
  'Signatures',

  // Large / easily refetched
  'Bucket',
  'BucketItem',
  'BucketMetaData',
  'ExploreFilesResponse'
]);

const isStorageAvailable = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage);

let persistor: CachePersistor<unknown> | null = null;

const buildPersistor = (): CachePersistor<unknown> | null => {
  if (!isStorageAvailable()) return null;

  return new CachePersistor({
    cache,
    storage: new LocalStorageWrapper(window.localStorage) as PersistentStorage<string>,
    key: STORAGE_KEY,
    maxSize: MAX_SIZE_BYTES,
    debug: process.env.NODE_ENV !== 'production',
    trigger: 'background',
    // Drop entries whose typename we don't want persisted before serialization.
    // The CachePersistor contract expects a Promise-returning mapper; we have
    // no awaitable work here so return a resolved Promise explicitly rather
    // than marking the function `async` (which would trigger require-await).
    persistenceMapper: (data: unknown) => {
      if (!data || typeof data !== 'object') return Promise.resolve(data);
      const entries = Object.entries(data as Record<string, any>);
      const filtered = entries.filter(([key, value]) => {
        // Apollo cache keys are either `__typename:id` or `ROOT_QUERY` etc.
        if (key.startsWith('ROOT_')) return true;
        // eslint-disable-next-line no-underscore-dangle
        const typename: string | undefined = value?.__typename;
        if (!typename) return true;
        return !NON_PERSISTED_TYPENAMES.has(typename);
      });
      return Promise.resolve(Object.fromEntries(filtered));
    }
  });
};

/**
 * Restore the persisted cache snapshot before the React tree mounts. Awaits the
 * read so the first render sees populated cache data instead of loading states.
 *
 * If the persisted version doesn't match the current build, the cache is purged
 * and a fresh empty cache is used. This is the safety net for schema migrations
 * and keyFields changes.
 */
export const restoreCache = async (): Promise<void> => {
  if (!isStorageAvailable()) return;
  if (!persistor) persistor = buildPersistor();
  if (!persistor) return;

  try {
    const persistedVersion = window.localStorage.getItem(SCHEMA_VERSION_KEY);
    const currentVersion = APPLICATION_VERSION ?? '1';

    if (persistedVersion !== currentVersion) {
      await persistor.purge();
      window.localStorage.setItem(SCHEMA_VERSION_KEY, currentVersion);
      return;
    }

    await persistor.restore();
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[restoreCache] failed, falling back to empty cache:', err);
    }
    try {
      await persistor.purge();
    } catch { /* nothing to do */
    }
  }
};

/**
 * Drop the persisted cache. Call from `handleLogout` — leaving a logged-out
 * user's cache on disk is both a privacy issue and a stale-data risk if the
 * next user signs in on the same browser.
 */
export const purgeCache = async (): Promise<void> => {
  if (!persistor) return;
  try {
    await persistor.purge();
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[purgeCache] failed:', err);
    }
  }
};

/** Pause writes (e.g. while a destructive operation is in flight). */
export const pauseCachePersistence = (): void => {
  persistor?.pause();
};

/** Resume writes. */
export const resumeCachePersistence = (): void => {
  persistor?.resume();
};
